// Global safety net for the micro-frontend "blank page after deploy" problem.
//
// After a redeploy, a client still running the previous build can request a
// hashed chunk that no longer exists. That surfaces as one of:
//   - a ChunkLoadError / failed dynamic import()
//   - a failed <script>/<link> resource load
//   - a "Unexpected token '<'" SyntaxError, because CloudFront rewrites the
//     missing-asset 403 into index.html (HTML) which the browser parses as JS
// Any of these leaves the app blank. Here we detect them and force exactly ONE
// reload so the browser fetches the fresh (no-cache) index.html and the current
// chunk URLs. A sessionStorage cooldown prevents reload loops.

const RELOAD_AT_KEY = 'idd:lastChunkReloadAt';
const RELOAD_COOLDOWN_MS = 15000; // never reload more than once per 15s (loop guard)
const OWN_ASSET_MARKER = '/main/latest/'; // only react to our own assets, not 3rd-party scripts

const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Loading script failed|Unexpected token '<'|ScriptExternalLoadError/i;

const isChunkError = (input) => {
  if (!input) return false;
  if (typeof input === 'string') return CHUNK_ERROR_RE.test(input);
  const name = input.name || '';
  if (name === 'ChunkLoadError' || name === 'ScriptExternalLoadError') return true;
  return CHUNK_ERROR_RE.test(input.message || '');
};

const reloadOnce = () => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_AT_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return; // already reloaded very recently
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch (e) {
    // sessionStorage blocked (private mode / sandboxed iframe) -> in-memory guard
    if (window.__iddChunkReloaded) return;
    window.__iddChunkReloaded = true;
  }
  window.location.reload();
};

let installed = false;

/**
 * Install global listeners that recover from stale-chunk failures by reloading once.
 * Call as early as possible (before the first dynamic import) so it can catch a
 * failed bootstrap chunk too.
 */
export const installChunkReload = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1) Failed dynamic import() / loadRemote() -> rejected promise.
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkError(event && event.reason)) reloadOnce();
  });

  // 2) Runtime errors + resource (<script>/<link>) load failures.
  //    Capture phase is required: resource error events do not bubble.
  window.addEventListener(
    'error',
    (event) => {
      const target = event && event.target;
      if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
        const url = target.src || target.href || '';
        if (url.includes(OWN_ASSET_MARKER)) reloadOnce();
        return;
      }
      if (isChunkError((event && event.error) || (event && event.message))) reloadOnce();
    },
    true
  );
};
